import { Box, Center, Flex, Spinner, VStack } from '@chakra-ui/react'
import { pipe } from '@helo/utils'

import {
  AnalyticsCard,
  type AnalyticsCardOutput,
} from '../../features/AnalyticsCard'
import type { ChartRenderConfig } from '../../features/Chart'
import { ChartAxis } from '../../features/Chart/ChartAxis'
import { ChartBar } from '../../features/Chart/ChartBar'
import { ChartCartesian } from '../../features/Chart/ChartCartesian'
import type { ChartComposerInput } from '../../features/Chart/ChartComposer'
import { ChartData } from '../../features/Chart/ChartData'
import { ChartDataLabel } from '../../features/Chart/ChartDataLabel'
import { ChartGrid } from '../../features/Chart/ChartGrid'
import { ChartInteraction } from '../../features/Chart/ChartInteraction'
import { ChartLegend } from '../../features/Chart/ChartLegend'
import { ChartLine } from '../../features/Chart/ChartLine'
import { ChartPie } from '../../features/Chart/ChartPie'
import { ChartPieConstants } from '../../features/Chart/ChartPie/ChartPie.constants'
import { ChartReferenceLine } from '../../features/Chart/ChartReferenceLine'
import { ChartSeries } from '../../features/Chart/ChartSeries'
import { ChartTooltip } from '../../features/Chart/ChartTooltip'
import { ChartWrapper } from '../../features/Chart/ChartWrapper'
import {
  Descriptor,
  type DescriptorInput,
  type DescriptorOutput,
} from '../../features/Descriptor'
import { Query } from '../../features/Query'
import { QueryStore } from '../../features/QueryStore'
import { RenderPipeline } from '../../features/RenderPipeline'
import { useAppStore } from '../../store/appStore/appStore'
import { createWidget } from '../widget-utils'

export interface AnalyticsChartWidgetProps
  extends DescriptorInput,
    Omit<ChartComposerInput, 'store'> {
  isLoading?: boolean
  'data-widget'?: string
}

type WidgetPipelineOutput = AnalyticsCardOutput &
  DescriptorOutput &
  ChartRenderConfig

export const AnalyticsChartWidget = ({
  isLoading: _isLoading,
  'data-widget': _dataWidget,
  ...options
}: AnalyticsChartWidgetProps) => {
  const queryStore = useAppStore(store => store.queryStore)

  const input: ChartComposerInput & DescriptorInput = {
    ...options,
    store: { queryStore },
  }

  const renderConfig = pipe(
    input,
    RenderPipeline.createContext(),
    QueryStore.setup(),
    Query.setup(),
    Descriptor.setup(),
    AnalyticsCard.setup(),
    ChartData.setup(),
    ChartSeries.setup(),
    ChartCartesian.setup(),
    ChartReferenceLine.setup(),
    ChartBar.setup(),
    ChartLine.setup(),
    ChartPie.setup(),
    ChartWrapper.setup(),
    ChartAxis.setup(),
    ChartDataLabel.setup(),
    ChartGrid.setup(),
    ChartLegend.setup(),
    ChartTooltip.setup(),
    RenderPipeline.getOutput(),
  ) as WidgetPipelineOutput

  if (renderConfig.pie != null) {
    return (
      <AnalyticsCard.Card renderConfig={renderConfig}>
        <AnalyticsCard.Header
          renderConfig={renderConfig}
          showDragHandle
          showActions
        />
        <AnalyticsCard.Content renderConfig={renderConfig}>
          <Flex
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
            columnGap={`clamp(${ChartPieConstants.minLegendGapPx}px, 4vw, ${ChartPieConstants.maxLegendGapPx}px)`}
            rowGap="24px"
            minW={0}
            flex="1"
            position="relative"
          >
            <ChartInteraction.Provider>
              <ChartPie.Slices renderConfig={renderConfig} />
            </ChartInteraction.Provider>
            {renderConfig.legend?.visible ? (
              <ChartLegend.Legend renderConfig={renderConfig} />
            ) : null}
            {renderConfig.isQueryLoading ? (
              <Center
                position="absolute"
                inset={0}
                pointerEvents="none"
                aria-hidden
              >
                <Spinner size="sm" color="presets.turquoise.100" />
              </Center>
            ) : null}
          </Flex>
        </AnalyticsCard.Content>
      </AnalyticsCard.Card>
    )
  }

  return (
    <AnalyticsCard.Card renderConfig={renderConfig}>
      <AnalyticsCard.Header
        renderConfig={renderConfig}
        showDragHandle
        showActions
      />
      <AnalyticsCard.Content renderConfig={renderConfig}>
        <VStack alignItems="stretch" gap="16px" minW={0}>
          <ChartLegend.Legend renderConfig={renderConfig} />
          <Box flex="1" minW={0} position="relative">
            <ChartInteraction.Provider>
              <ChartWrapper.Wrapper renderConfig={renderConfig}>
                <ChartGrid.Grid renderConfig={renderConfig} />
                <ChartAxis.XAxis renderConfig={renderConfig} />
                <ChartAxis.YAxis renderConfig={renderConfig} />
                <ChartTooltip.HoverGuide renderConfig={renderConfig} />
                <ChartData.SeriesRenderer
                  renderConfig={renderConfig}
                  visualizations={{
                    line: ChartLine.Line,
                    bar: ChartBar.Bar,
                  }}
                />
                <ChartDataLabel.DataLabels renderConfig={renderConfig} />
                <ChartTooltip.Tooltip renderConfig={renderConfig} />
              </ChartWrapper.Wrapper>
            </ChartInteraction.Provider>
            {renderConfig.isQueryLoading ? (
              <Center
                position="absolute"
                inset={0}
                pointerEvents="none"
                aria-hidden
              >
                <Spinner size="sm" color="presets.turquoise.100" />
              </Center>
            ) : null}
          </Box>
        </VStack>
      </AnalyticsCard.Content>
    </AnalyticsCard.Card>
  )
}

export const AnalyticsChartWidgetDefinition = createWidget(
  AnalyticsChartWidget,
  {
    type: 'AnalyticsChartWidget',
    slots: [],
  },
)
